import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { Operation } from '../../modules/organization/models/operation';
import { Store } from '@ngxs/store';
import { AuthState } from '@core/store/auth/auth.state';

@Injectable({
  providedIn: 'root'
})
export class OperationSelectionService {
  private selectedOperationSource = new BehaviorSubject<Operation | { id: string; name: string }>({ id: '', name: 'All' });
  selectedOperation$ = this.selectedOperationSource.asObservable();
  private isTenancyLevelSelection = false;
  private customerContractOperation: string = null;
  private supplierContractOperation: string = null;

  constructor(private store: Store) {}

  setSelectedOperation(operation: Operation | { id: string; name: string }): void {
    if (operation) {
      this.selectedOperationSource.next(operation);
      this.isTenancyLevelSelection = operation.id !== '';
    }
  }

  getSelectedOperation(): Operation | { id: string; name: string } {
    return this.selectedOperationSource.value;
  }

  isTenancySelection(): boolean {
    return this.isTenancyLevelSelection;
  }

  getCurrentUserOperations(): Observable<Operation[]> {
    return this.store.select(AuthState.getCurrentUserOperations);
  }

  getCurrentUserOperationIds(): Observable<string[]> {
    return this.getCurrentUserOperations().pipe(
      map(operations => operations.map(op => op.id))
    );
  }

  // The selected operation, or every operation of the current user when "All" is selected
  getOperationIdsToFilter(): Observable<string[]> {
    const selectedOperationId = this.selectedOperationSource.value?.id;
    if (selectedOperationId) {
      return of([selectedOperationId]);
    }
    return this.getCurrentUserOperations().pipe(
      take(1),
      map(operations => (operations || []).map(op => op.id).filter(Boolean))
    );
  }

  determineOperationToUse(formOperation: string, readOnly: boolean, contractType: 'customer' | 'supplier'): string {
    if (readOnly) {
      return this.selectedOperationSource.value.id;
    } else if (formOperation) {
      const operationToUse = formOperation;
      if (contractType === 'customer') {
        this.setCustomerContractOperation(operationToUse);
      } else {
        this.setSupplierContractOperation(operationToUse);
      }
      return operationToUse;
    } else {
      const localOperation = contractType === 'customer' ? 
        this.getCustomerContractOperation() : 
        this.getSupplierContractOperation();
      
      return localOperation || 'All';
    }
  }

  setCustomerContractOperation(operationId: string): void {
    this.customerContractOperation = operationId;
  }

  getCustomerContractOperation(): string {
    return !this.isTenancyLevelSelection ? this.customerContractOperation : null;
  }

  setSupplierContractOperation(operationId: string): void {
    this.supplierContractOperation = operationId;
  }

  getSupplierContractOperation(): string {
    return !this.isTenancyLevelSelection ? this.supplierContractOperation : null;
  }

  clearAllOperations(): void {
    this.customerContractOperation = null;
    this.supplierContractOperation = null;
  }
}
